import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from './api';
import { useSaveStatus } from './components/ui';

/** Lädt den vollständigen Fall (alle Ebenen) und erlaubt gezieltes Neuladen. */
export function useCaseData(caseId: string | undefined) {
  const [data, setData] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!caseId) return;
    try {
      const result = await api.get(`/api/v1/cases/${caseId}`);
      setData(result);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'INTERNAL');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    setLoading(true);
    void reload();
  }, [reload]);

  return { data, error, loading, reload };
}

export interface FieldErrors {
  [path: string]: string;
}

function toFieldErrors(e: unknown): { code: string; fields: FieldErrors } {
  if (e instanceof ApiError) {
    const fields: FieldErrors = {};
    for (const f of e.fields ?? []) fields[f.path] = f.message;
    return { code: e.code, fields };
  }
  return { code: 'INTERNAL', fields: {} };
}

/** CRUD-Aktionen für Subressourcen mit Speicherstatus-Anbindung. */
export function useEntityActions(caseId: string, entityPath: string, onDone: () => void) {
  const status = useSaveStatus();
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const run = useCallback(
    async (fn: () => Promise<unknown>): Promise<boolean> => {
      setFieldErrors({});
      setErrorCode(null);
      status.saving();
      try {
        await fn();
        status.saved();
        onDone();
        return true;
      } catch (e) {
        status.error();
        const { code, fields } = toFieldErrors(e);
        setErrorCode(code);
        setFieldErrors(fields);
        return false;
      }
    },
    [onDone, status],
  );

  return {
    fieldErrors,
    errorCode,
    create: (body: unknown) => run(() => api.post(`/api/v1/cases/${caseId}/${entityPath}`, body)),
    update: (id: string, body: unknown) =>
      run(() => api.patch(`/api/v1/cases/${caseId}/${entityPath}/${id}`, body)),
    remove: (id: string) => run(() => api.del(`/api/v1/cases/${caseId}/${entityPath}/${id}`)),
    custom: (fn: () => Promise<unknown>) => run(fn),
  };
}

/** Autosave für Fall-Stammdaten: debounced PATCH mit sichtbarem Status (Auftrag 8). */
export function useCaseAutosave(caseId: string, reload: () => void) {
  const status = useSaveStatus();
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const pending = useRef<Record<string, unknown>>({});

  const queue = useCallback(
    (patch: Record<string, unknown>) => {
      pending.current = { ...pending.current, ...patch };
      if (timer.current) clearTimeout(timer.current);
      status.saving();
      timer.current = setTimeout(async () => {
        const body = pending.current;
        pending.current = {};
        try {
          await api.patch(`/api/v1/cases/${caseId}`, body);
          status.saved();
          reload();
        } catch {
          status.error();
        }
      }, 700);
    },
    [caseId, reload, status],
  );

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  return queue;
}

/** Datumswert (ISO) → Wert für <input type="date"> */
export function dateInput(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : '';
}
